/**
 * Details of the selected node in the graph lights side panel (plan section 12, item 3).
 *
 * A cause shows what the old diagnosis map's cause detail showed: the write-up from GET /api/v5/brain/cause_detail
 * (summary, mechanism, lines, checks, fix, evidence folds, track record, sources; useCauseDetail) and the live chat state from
 * tse:diag_state (confidence, how the percentage was reached from `explain`, clues for and against, ruled-out reason).
 * A symptom or fact shows its observed value and the causes it supports or rules out, each a button that selects that cause.
 */
import React from "react";
import { ArrowLeft } from "lucide-react";

import EdgeDetails, { EdgeList } from "./EdgeDetails";
import { EDGE_KINDS, ROLES, TONES } from "./lightsConstants";
import { LIGHTS_TEXT, formatPercent } from "./lightsText";
import useCauseDetail, { DETAIL_STATUS } from "./useCauseDetail";

const T = LIGHTS_TEXT.detail;
const list = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? null : Number(v));
const NUM_DIGITS = 2;
const NO_FACTOR = 1;
const WRITEUP_PENDING = "pending";
const FIX_ESCALATION = "escalation";
const CHECK_CURRENT = "current";
const CHECK_DONE = "done";
const CHECK_FAILED = "failed";
const CHECK_PENDING = "pending";
const ICON_PX = 14;

/** Clean the "[runbook]" tags the write-ups carry (rhs4_causedetail RUNBOOK_TAG). */
const tidy = (t) => String(t === null || t === undefined ? "" : t).replace(/\s*\[[^\]]*\brunbook\]\s*/gi, " ").trim();

function Section({ title, children }) {
  return (
    <section className="d5-lights-sec">
      <h4 className="d5-lights-label">{title}</h4>
      {children}
    </section>
  );
}

function Fold({ title, count, children }) {
  return (
    <details className="d5-lights-fold">
      <summary>{title}{count != null && <span className="d5-lights-count">{count}</span>}</summary>
      {children}
    </details>
  );
}

function NodeButton({ nid, lights, onSelect, children }) {
  const node = lights.nodes.get(nid);
  const label = children || node?.label || nid;
  if (!node) return <span>{label}</span>;
  return <button type="button" className="d5-lights-link" onClick={() => onSelect({ node: nid })}>{label}</button>;
}

/** The percentage chain of rhs4_explain.js chainText, from the server's `explain` keys (score_explain.py). */
function chainText(cause) {
  const e = cause?.explain;
  if (!e || typeof e !== "object" || num(e.score) === null) return null;
  const open = num(e.open_count);
  const adjust = num(e.adjust_factor);
  const renorm = num(e.renorm) ?? NO_FACTOR;
  const adjusted = adjust !== null && adjust !== NO_FACTOR;
  const reshared = Math.abs(renorm - NO_FACTOR) > 1e-9;
  const share = num(e.share_before_adjust);
  return `Score ${num(e.score).toFixed(NUM_DIGITS)} → share of ${open !== null ? `all ${open} open causes` : "the open causes"}`
    + ` = ${share !== null ? `${Math.round(share * 100)} %` : "–"}${adjusted ? ` → ×${+adjust.toPrecision(3)}` : ""}`
    + `${reshared ? " → shared out again" : ""} = ${formatPercent(cause.percentage || 0)}`;
}

function checkStatus(step, cause, diag, observedIds) {
  const known = list(cause?.checks).find((c) => step.key && c.step_id === step.key);
  if (known && T.checkWord[known.status]) return known.status;
  if (diag?.step?.step_id && diag.step.step_id === step.key) return CHECK_CURRENT;
  if (step.confirm_fact && observedIds.has(step.confirm_fact)) return CHECK_DONE;
  if (step.deny_fact && observedIds.has(step.deny_fact)) return CHECK_FAILED;
  return CHECK_PENDING;
}

function Head({ node, cause, ruled, data, lines }) {
  const text = data.summary || data.cause;
  const status = node.status && LIGHTS_TEXT.causeStatus[node.status];
  return (
    <div className="d5-lights-head">
      <h3 className="d5-lights-detail-title">{tidy(data.label) || node.label}</h3>
      <p className="d5-lights-conf">
        {ruled || node.tone === TONES.EMBER ? T.ruledHere : cause ? T.confidence(cause.percentage || 0) : T.notInChat}
        {status && !ruled && <span className={`d5-lights-chip d5-lights-chip--${node.status}`}>{status}</span>}
      </p>
      {lines.length > 0 && (
        <div className="d5-lights-lines">
          {lines.map((l) => <span key={l.id || l.label} className="d5-lights-line" title={tidy(l.description) || undefined}>{tidy(l.label)}</span>)}
        </div>
      )}
      {text && <p className="d5-lights-summary">{tidy(text)}</p>}
      {data.mechanism && <p className="d5-lights-muted">{T.why}{tidy(data.mechanism)}</p>}
    </div>
  );
}

function InChat({ cause, ruled, observedIds, lights, onSelect }) {
  const clues = list(cause?.clues).filter((c) => observedIds.has(c.fact_id));
  const chain = chainText(cause);
  return (
    <Section title={T.inChat}>
      {ruled && <p>{T.ruledHere}{ruled.reason ? ` · ${tidy(ruled.reason)}` : ""}</p>}
      {ruled?.by_fact && (
        <ul className="d5-lights-facts">
          <li><span className="d5-lights-no" aria-label={T.against}>{T.checkMark.failed}</span> <NodeButton nid={ruled.by_fact} lights={lights} onSelect={onSelect} /></li>
        </ul>
      )}
      {!ruled && cause?.reason && <p className="d5-lights-muted">{T.reason}: {tidy(cause.reason)}</p>}
      {clues.length > 0 && (
        <ul className="d5-lights-facts">
          {clues.map((c) => (
            <li key={c.fact_id}>
              <span className="d5-lights-ok" aria-label={T.pointsTo}>{T.checkMark.done}</span>{" "}
              <NodeButton nid={c.fact_id} lights={lights} onSelect={onSelect}>{tidy(c.label) || lights.nodes.get(c.fact_id)?.label || c.fact_id}</NodeButton>
              <span className="d5-lights-muted"> · {c.book && !c.seen ? T.fromGuide : T.seen(Number(c.seen) || 0, Number(c.wins) || 0)}</span>
            </li>
          ))}
        </ul>
      )}
      {!ruled && cause && (
        <p className="d5-lights-muted">
          <b>{T.howReached(cause.percentage || 0)}</b><br />{chain || T.noChain}
        </p>
      )}
    </Section>
  );
}

function Checks({ data, cause, diag, observedIds }) {
  const steps = list(data.steps).slice().sort((a, b) => (num(a.order) || 0) - (num(b.order) || 0));
  if (!steps.length) return <Section title={T.checks}><p className="d5-lights-muted">{T.noChecks}</p></Section>;
  return (
    <Section title={T.checks}>
      <ol className="d5-lights-steps">
        {steps.map((step, i) => {
          const st = checkStatus(step, cause, diag, observedIds);
          const when = [step.confirm_label ? `Confirms if ${tidy(step.confirm_label)}` : "", step.deny_label ? `rules out if ${tidy(step.deny_label)}` : ""].filter(Boolean).join(" · ");
          return (
            <li key={step.key || i} className={`d5-lights-step d5-lights-step--${st}`}>
              <span className="d5-lights-mark" aria-label={T.checkWord[st]}>{T.checkMark[st]}</span>
              <div>
                <div>{tidy(step.text)}</div>
                {when && <div className="d5-lights-muted">{when}</div>}
              </div>
            </li>
          );
        })}
      </ol>
    </Section>
  );
}

function Fix({ data }) {
  const fix = data.fix || {};
  const parts = list(fix.parts).filter((p) => p && p.text);
  if (!fix.text && !parts.length) return null;
  const tried = num(fix.tried);
  return (
    <Section title={fix.kind === FIX_ESCALATION ? T.escalation : T.fix}>
      {parts.length > 1
        ? <ul className="d5-lights-list">{parts.map((p, i) => <li key={i}>{p.runbook && <b>{tidy(p.runbook)}: </b>}{tidy(p.text)}</li>)}</ul>
        : <p>{tidy(parts.length ? parts[0].text : fix.text)}</p>}
      {data.root_cause?.label && <p className="d5-lights-muted">{T.rootCause}: {tidy(data.root_cause.label)}</p>}
      {tried ? <p className="d5-lights-muted">{T.tried(tried, num(fix.worked) || 0)}</p> : null}
    </Section>
  );
}

function Evidence({ data, lights, onSelect }) {
  const supports = list(data.supports);
  const rules = list(data.rules_out);
  return (
    <>
      {supports.length > 0 && (
        <Fold title={T.supports} count={supports.length}>
          <ul className="d5-lights-list">
            {supports.map((r, i) => (
              <li key={r.nid || i}>
                <b><NodeButton nid={r.nid} lights={lights} onSelect={onSelect}>{tidy(r.label) || lights.nodes.get(r.nid)?.label || r.nid}</NodeButton></b>
                {r.question && <span className="d5-lights-muted"> · {tidy(r.question)}</span>}
                <div className="d5-lights-muted">{num(r.seen) ? T.seen(num(r.seen), num(r.wins) || 0) : r.book ? T.fromGuide : T.seen(0, 0)}</div>
              </li>
            ))}
          </ul>
        </Fold>
      )}
      {rules.length > 0 && (
        <Fold title={T.rulesOut} count={rules.length}>
          <ul className="d5-lights-list">
            {rules.map((r, i) => (
              <li key={r.nid || i}>
                <b><NodeButton nid={r.nid} lights={lights} onSelect={onSelect}>{tidy(r.label) || lights.nodes.get(r.nid)?.label || r.nid}</NodeButton></b>
                {r.why && <div className="d5-lights-muted">{tidy(r.why)}</div>}
              </li>
            ))}
          </ul>
        </Fold>
      )}
    </>
  );
}

function Track({ data, bundleCause }) {
  const rec = { ...(bundleCause || {}), ...(data.record || {}) };
  const cases = num(rec.cases);
  const trust = num(rec.trust);
  const sources = list(data.sources);
  const merged = list(data.merged_from);
  return (
    <>
      <Fold title={T.track} count={trust != null ? trust.toFixed(NUM_DIGITS) : null}>
        <p>{T.trackLine(cases || 0, trust)}{rec.last_seen ? ` · ${T.lastSeen(rec.last_seen)}` : ""}</p>
      </Fold>
      {(sources.length > 0 || merged.length > 0) && (
        <Fold title={T.sources} count={sources.length + merged.length}>
          <ul className="d5-lights-list">
            {sources.map((x, i) => <li key={`s${i}`}>{tidy(x.title || x.slug || "")}</li>)}
            {merged.map((x, i) => <li key={`m${i}`}>{tidy(x.label)}{x.source && <span className="d5-lights-muted"> · {tidy(x.source)}</span>}</li>)}
          </ul>
        </Fold>
      )}
    </>
  );
}

function CauseDetails({ node, lights, diagState, bundle, learnedKey, onSelect, lineLabel }) {
  const { status, data: raw } = useCauseDetail(node.nid, learnedKey);
  const data = raw || {};
  const cause = list(diagState?.causes).find((c) => c.key === node.nid) || null;
  const ruled = list(diagState?.ruled_out).find((r) => r.cause_key === node.nid) || null;
  const observedIds = new Set(list(diagState?.observed).map((o) => o.id));
  const bundleCause = list(bundle?.causes).find((c) => c.cause_key === node.nid) || null;
  const lines = list(data.lods).length ? data.lods : lineLabel ? [{ id: lineLabel, label: lineLabel }] : [];
  const loading = status === DETAIL_STATUS.LOADING || status === DETAIL_STATUS.IDLE;
  return (
    <div className="d5-lights-detail" aria-busy={loading || undefined}>
      <Head node={node} cause={cause} ruled={ruled} data={data} lines={lines} />
      {loading && <p className="d5-lights-muted">{T.loading}</p>}
      {status === DETAIL_STATUS.FAIL && <p className="d5-lights-muted">{T.unavailable}</p>}
      {data.writeup?.status === WRITEUP_PENDING && <p className="d5-lights-muted">{T.writing}</p>}
      <InChat cause={cause} ruled={ruled} observedIds={observedIds} lights={lights} onSelect={onSelect} />
      {status === DETAIL_STATUS.OK && (
        <>
          <Checks data={data} cause={cause} diag={diagState} observedIds={observedIds} />
          <Fix data={data} />
          <Evidence data={data} lights={lights} onSelect={onSelect} />
          <Track data={data} bundleCause={bundleCause} />
        </>
      )}
      <EdgeList nid={node.nid} lights={lights} onSelect={onSelect} />
    </div>
  );
}

function LeafDetails({ node, lights, onSelect, lineLabel }) {
  const out = lights.edges.filter((e) => e.src === node.nid);
  const supports = out.filter((e) => e.kind === EDGE_KINDS.SUPPORTS);
  const rules = out.filter((e) => e.kind === EDGE_KINDS.RULES_OUT);
  const row = (e) => {
    const cause = lights.nodes.get(e.dst);
    if (!cause) return null;
    const status = cause.status && LIGHTS_TEXT.causeStatus[cause.status];
    return (
      <li key={e.id} className={e.traversed ? "d5-lights-traversed" : ""}>
        <NodeButton nid={e.dst} lights={lights} onSelect={onSelect} />
        {cause.pct != null && cause.tone !== TONES.EMBER && <span className="d5-lights-pct">{formatPercent(cause.pct)}</span>}
        {status && <span className={`d5-lights-chip d5-lights-chip--${cause.status}`}>{status}</span>}
        <button type="button" className="d5-lights-edge-btn" onClick={() => onSelect({ edge: e.id })} aria-label={LIGHTS_TEXT.edge.openEdge} title={LIGHTS_TEXT.edge.openEdge}>
          <span className={`d5-lights-edge-swatch d5-lights-edge-swatch--${e.kind === EDGE_KINDS.RULES_OUT ? "rules_out" : e.traversed ? "lit" : "faint"}`} aria-hidden="true" />
        </button>
      </li>
    );
  };
  return (
    <div className="d5-lights-detail">
      <div className="d5-lights-head">
        <h3 className="d5-lights-detail-title">{node.label}</h3>
        <p className="d5-lights-conf">
          {LIGHTS_TEXT.roles[node.role]}
          {lineLabel && <span className="d5-lights-line">{lineLabel}</span>}
        </p>
        <p>{node.observed ? `${LIGHTS_TEXT.observedValue[node.observed.value] || LIGHTS_TEXT.observedValue.unknown}${node.observed.source ? ` · ${LIGHTS_TEXT.observedSource(node.observed.source)}` : ""}` : T.notObserved}</p>
      </div>
      {!out.length && <p className="d5-lights-muted">{T.noRelations}</p>}
      {supports.length > 0 && <Section title={`${T.supportsCauses} (${supports.length})`}><ul className="d5-lights-causes">{supports.map(row)}</ul></Section>}
      {rules.length > 0 && <Section title={`${T.rulesOutCauses} (${rules.length})`}><ul className="d5-lights-causes">{rules.map(row)}</ul></Section>}
    </div>
  );
}

/** `selection` = {node: nid} | {edge: id}; `onSelect(selection)` navigates, `onBack()` returns to the lists. */
export default function NodeDetails({ selection, lights, layout, diagState, bundle, learnedKey, onSelect, onBack }) {
  if (selection.edge) {
    const edge = lights.edgeById.get(selection.edge);
    return edge ? <EdgeDetails edge={edge} lights={lights} diagState={diagState} onSelect={onSelect} onBack={onBack} /> : null;
  }
  const node = lights.nodes.get(selection.node);
  if (!node) return null;
  const clusterId = layout.clusterOf.get(node.nid);
  const lineLabel = clusterId ? layout.clusters.find((c) => c.id === clusterId)?.label || null : null;
  return (
    <div className="d5-lights-panel-body">
      <button type="button" className="d5-lights-btn d5-lights-back" onClick={onBack}>
        <ArrowLeft size={ICON_PX} aria-hidden="true" />
        <span>{LIGHTS_TEXT.back}</span>
      </button>
      {node.role === ROLES.CAUSE
        ? <CauseDetails node={node} lights={lights} diagState={diagState} bundle={bundle} learnedKey={learnedKey} onSelect={onSelect} lineLabel={lineLabel} />
        : <LeafDetails node={node} lights={lights} onSelect={onSelect} lineLabel={lineLabel} />}
    </div>
  );
}
